import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import LocalStorage from "../Helpers/LocalStorage";
import { useTranslate } from "@tolgee/react";

const POLL_INTERVAL = 2000;
const PPTX_CONTENT_TYPE =
  "application/vnd.openxmlformats-officedocument.presentationml.presentation";
const API_URL = process.env.REACT_APP_API_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${LocalStorage.getAuthenticationToken()}`,
});

// Persists state across SPA navigation within the same tab
let _persistedJobs = [];
let _persistedIsProcessing = false;
let _persistedCurrentIndex = -1;
let _persistedLanguages = [];
let _persistedFile = null;
let _persistedApiProjectId = null;

const usePresentationTranslation = (apiProjectId) => {
  const { t } = useTranslate();
  const [jobs, setJobsRaw] = useState(_persistedJobs);
  const [isProcessing, setIsProcessingRaw] = useState(_persistedIsProcessing);
  const pollRef = useRef(null);
  const fileRef = useRef(_persistedFile);
  const languagesRef = useRef(_persistedLanguages);
  const currentIndexRef = useRef(_persistedCurrentIndex);

  const setJobs = useCallback((updater) => {
    setJobsRaw((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      _persistedJobs = next;
      return next;
    });
  }, []);

  const setIsProcessing = useCallback((val) => {
    _persistedIsProcessing = val;
    setIsProcessingRaw(val);
  }, []);

  const cleanup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const updateJob = useCallback(
    (index, patch) => {
      setJobs((prev) => prev.map((j, i) => (i === index ? { ...j, ...patch } : j)));
    },
    [setJobs]
  );

  const runJob = useCallback(
    async (index) => {
      const file = fileRef.current;
      const language = languagesRef.current[index];
      if (!file || !language) return;

      currentIndexRef.current = index;
      _persistedCurrentIndex = index;
      updateJob(index, { status: "uploading" });

      try {
        const params = {
          target_language: language,
          original_filename: file.name,
        };
        if (apiProjectId) params.project_id = apiProjectId;

        const { data } = await axios.post(
          `${API_URL}api/v1/connector/presentation_translations`,
          params,
          { headers: authHeaders() }
        );

        updateJob(index, { jobId: data.job_id });

        await fetch(data.upload_url, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": PPTX_CONTENT_TYPE },
        });

        updateJob(index, { status: "processing" });

        await axios
          .put(
            `${API_URL}api/v1/connector/presentation_translations/${data.job_id}/start`,
            apiProjectId ? { project_id: apiProjectId } : {},
            { headers: authHeaders() }
          )
          .catch((err) => {
            const detail = err?.response?.data?.error || err?.response?.data?.detail;
            throw Object.assign(new Error(detail || "start_failed"), { _detail: detail });
          });

        pollRef.current = setInterval(async () => {
          try {
            const { data: statusData } = await axios.get(
              `${API_URL}api/v1/connector/presentation_translations/${data.job_id}`,
              { headers: authHeaders(), params: apiProjectId ? { project_id: apiProjectId } : undefined }
            );

            if (statusData.status === "processing" && statusData.progress) {
              updateJob(index, { progress: statusData.progress });
            }

            if (statusData.status === "completed") {
              clearInterval(pollRef.current);
              pollRef.current = null;
              updateJob(index, { status: "completed", downloadUrl: statusData.download_url });
            }

            if (statusData.status === "failed") {
              clearInterval(pollRef.current);
              pollRef.current = null;
              updateJob(index, {
                status: "failed",
                error: statusData.error_message || "translation_failed",
              });
            }
          } catch {
            clearInterval(pollRef.current);
            pollRef.current = null;
            updateJob(index, { status: "failed", error: "connection_lost" });
          }
        }, POLL_INTERVAL);
      } catch (err) {
        cleanup();
        updateJob(index, {
          status: "failed",
          error: err?._detail || err?.response?.data?.error || err.message || "upload_failed",
        });
      }
    },
    [apiProjectId, cleanup, updateJob]
  );

  // On mount, resume polling for an in-flight job if one exists in persisted state
  useEffect(() => {
    const idx = _persistedCurrentIndex;
    if (idx < 0 || !_persistedJobs[idx]) return;
    const job = _persistedJobs[idx];
    if (job.status !== "processing" || !job.jobId || pollRef.current) return;

    pollRef.current = setInterval(async () => {
      try {
        const { data: statusData } = await axios.get(
          `${API_URL}api/v1/connector/presentation_translations/${job.jobId}`,
          { headers: authHeaders(), params: _persistedApiProjectId ? { project_id: _persistedApiProjectId } : undefined }
        );

        if (statusData.status === "processing" && statusData.progress) {
          updateJob(idx, { progress: statusData.progress });
        }

        if (statusData.status === "completed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          updateJob(idx, { status: "completed", downloadUrl: statusData.download_url });
        }

        if (statusData.status === "failed") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          updateJob(idx, { status: "failed", error: statusData.error_message || "translation_failed" });
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        updateJob(idx, { status: "failed", error: "connection_lost" });
      }
    }, POLL_INTERVAL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watch for job completions/failures to advance the queue
  useEffect(() => {
    if (!isProcessing || jobs.length === 0) return;

    const idx = currentIndexRef.current;
    if (idx < 0) return;

    const current = jobs[idx];
    if (!current) return;
    if (current.status !== "completed" && current.status !== "failed") return;

    const nextIndex = jobs.findIndex((j, i) => i > idx && j.status === "pending");

    if (nextIndex !== -1) {
      runJob(nextIndex);
    } else {
      setIsProcessing(false);
      currentIndexRef.current = -1;
      _persistedCurrentIndex = -1;
    }
  }, [jobs, isProcessing, runJob, setIsProcessing]);

  const startTranslation = useCallback(
    (file, languages) => {
      if (!apiProjectId) {
        const failedJobs = languages.map((lang) => ({
          language: lang,
          status: "failed",
          progress: { extracted: 0, translated: 0, total: 0 },
          downloadUrl: null,
          error: t("presentation_tool.error_no_project"),
          jobId: null,
        }));
        setJobs(failedJobs);
        return;
      }

      cleanup();
      fileRef.current = file;
      languagesRef.current = languages;
      _persistedFile = file;
      _persistedLanguages = languages;
      _persistedApiProjectId = apiProjectId;

      const initialJobs = languages.map((lang) => ({
        language: lang,
        status: "pending",
        progress: { extracted: 0, translated: 0, total: 0 },
        downloadUrl: null,
        error: null,
        jobId: null,
      }));

      setJobs(initialJobs);
      setIsProcessing(true);
      currentIndexRef.current = 0;
      _persistedCurrentIndex = 0;

      setTimeout(() => runJob(0), 0);
    },
    [apiProjectId, cleanup, runJob, setJobs, setIsProcessing, t]
  );

  const reset = useCallback(() => {
    cleanup();
    setJobs([]);
    setIsProcessing(false);
    currentIndexRef.current = -1;
    _persistedCurrentIndex = -1;
    fileRef.current = null;
    languagesRef.current = [];
    _persistedFile = null;
    _persistedLanguages = [];
    _persistedApiProjectId = null;
  }, [cleanup, setJobs, setIsProcessing]);

  const triggerDownload = (url) => {
    const a = document.createElement("a");
    a.href = url;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = useCallback(() => {
    const urls = jobs.filter((j) => j.downloadUrl).map((j) => j.downloadUrl);
    urls.forEach((url, i) => {
      setTimeout(() => triggerDownload(url), i * 400);
    });
  }, [jobs]);

  return { jobs, isProcessing, startTranslation, reset, downloadAll };
};

export default usePresentationTranslation;
